import { forwardRef, type TextareaHTMLAttributes } from 'react'

/* ==========================================================================
   Textarea Component
   Based on design.md specifications
   Task F30: Updated to use pharmacy colors for focus states
   ========================================================================== */

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Error state styling */
  error?: boolean
}

/**
 * Multi-line text input with focus, disabled, and error states.
 * Uses the same styling patterns as Input.
 *
 * @example
 * ```tsx
 * <Textarea placeholder="Enter notes..." />
 * <Textarea rows={5} disabled />
 * <Textarea error placeholder="Invalid input" />
 * ```
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ error = false, disabled, className = '', rows = 4, ...props }, ref) => {
    const baseStyles = [
      // Layout
      'w-full',
      'px-3 py-3',
      // Typography
      'text-base',
      'font-sans',
      'text-[var(--color-charcoal)]',
      'placeholder:text-[var(--color-gray-500)]',
      // Background and border
      'bg-white',
      'border',
      'rounded-md',
      // Resize behavior
      'resize-y',
      'min-h-[80px]',
      // Transitions
      'transition-[border-color,box-shadow] duration-150 ease-in-out',
      // Focus state
      'focus:outline-none',
    ]

    const stateStyles = error
      ? [
          // Error state (not customizable per design.md)
          'border-[var(--color-error)]',
          'focus:border-[var(--color-error)]',
          'focus:shadow-[0_0_0_3px_rgba(198,40,40,0.2)]',
        ]
      : [
          // Default state - uses pharmacy colors for white-labeling (Task F30)
          'border-[var(--color-gray-300)]',
          'focus:border-[var(--pharmacy-primary)]',
          'focus:shadow-[0_0_0_3px_var(--pharmacy-focus-ring)]',
        ]

    const disabledStyles = disabled
      ? [
          'bg-[var(--color-gray-100)]',
          'text-[var(--color-gray-500)]',
          'cursor-not-allowed',
          'resize-none',
        ]
      : []

    const classes = [
      ...baseStyles,
      ...stateStyles,
      ...disabledStyles,
      className,
    ]
      .filter(Boolean)
      .join(' ')

    return (
      <textarea
        ref={ref}
        rows={rows}
        disabled={disabled}
        className={classes}
        aria-invalid={error || undefined}
        {...props}
      />
    )
  }
)

Textarea.displayName = 'Textarea'

export default Textarea
